import { inject, Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { getCollections, TenantAuthClaims } from '@models';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { APP_TENANT_ID } from './app-tenant-id.token';

@Injectable({
  providedIn: 'root',
})
export class TenantContextService {
  private tenantIdSubject = new BehaviorSubject<string | null>(null);

  /**
   * undefined = auth not yet resolved (initial state)
   * null      = user logged out / no session
   * object    = claims loaded after login
   */
  private claimsSubject = new BehaviorSubject<
    TenantAuthClaims | null | undefined
  >(undefined);

  public tenantId$ = this.tenantIdSubject.pipe(
    filter((id): id is string => id !== null && id !== undefined),
  );

  public claims$ = this.claimsSubject.pipe(
    filter((c): c is TenantAuthClaims => c !== null && c !== undefined),
  );

  /** Emits once auth is resolved: null = no session, object = logged in */
  public claimsOrNull$ = this.claimsSubject.pipe(
    filter((c): c is TenantAuthClaims | null => c !== undefined),
  );

  public isSuperAdmin$ = this.claimsSubject.pipe(
    map((c) => c?.superAdmin === true),
  );

  /**
   * Emits the federation tenant ID when the active tenant is a club federated
   * to a federation. Emits null when there is no federation link.
   * Source: `federationId` custom claim (set by Cloud Functions when linking).
   */
  public federationId$ = this.claimsSubject.pipe(
    map((c) => c?.federationId ?? null),
  );

  /**
   * The primary role of the user in the active tenant.
   * Reacts to both claims changes and explicit tenant switches via switchTenant().
   * For secondary roles (e.g. DELEGATE in addition to PLAYER), use `hasRole()` or `getAllRoles()`.
   */
  public activeRole$ = combineLatest([
    this.tenantIdSubject,
    this.claimsSubject,
  ]).pipe(
    map(([tenantId, c]) => {
      if (!c) return null;
      if (tenantId && c.tenants?.[tenantId]) {
        return c.tenants[tenantId];
      }
      return null;
    }),
  );

  /**
   * All tenants the current user belongs to, with their primary roles.
   * Use this to build a tenant switcher UI.
   * Emits an empty array when the user is not logged in.
   */
  public allTenants$ = this.claimsSubject.pipe(
    map((c) => {
      if (!c?.tenants) return [];
      return Object.entries(c.tenants).map(([id, role]) => ({ id, role }));
    }),
  );

  /** True when a tenant (federation or club) is logged in */
  public isTenantUser$ = this.claimsSubject.pipe(
    map((c) => {
      if (!c) return false;
      return !!c.tenants && Object.keys(c.tenants).length > 0;
    }),
  );

  /** True only when the logged-in tenant is a club */
  public isClub$ = this.claimsSubject.pipe(
    map((c) => c?.tenantType === 'club'),
  );

  private readonly auth = inject(Auth);
  /** tenantId this binary was built for. Null in admin panel. */
  private readonly appTenantId =
    inject(APP_TENANT_ID, { optional: true }) ?? null;

  /**
   * Mobile app only: emits whether the currently authenticated user is a member
   * of the tenant this binary was built for (`appTenantId`).
   *
   *  - `true`  → no session resolved yet, no `appTenantId` (admin), or the user
   *              is a member / superadmin (normal flow, app must NOT block).
   *  - `false` → a session is resolved and the user is NOT a member of
   *              `appTenantId` (cross-tenant login → app must warn + logout).
   *
   * Defaults to `true` so that the app never blocks while auth is unresolved or
   * for users that are not logged in at all (public reads must keep working).
   */
  public belongsToAppTenant$ = this.claimsSubject.pipe(
    map((c) => {
      if (!this.appTenantId) return true; // admin / multi-tenant portal
      if (c === undefined || c === null) return true; // unresolved / logged out
      // STRICT membership: superAdmin is NOT exempt — a superadmin with no role
      // in this app's tenant is not a member of THIS app and must be blocked.
      return !!c.tenants?.[this.appTenantId];
    }),
  );

  constructor() {
    // If the binary was built for a specific tenant (mobile app), initialize
    // tenantId immediately so public Firestore reads work before login.
    if (this.appTenantId) {
      this.tenantIdSubject.next(this.appTenantId);
    }
  }

  /**
   * Called by AuthService after login / token refresh.
   *
   * Active tenant selection strategy:
   *  1. Mobile app:  always use `environment.tenantId` (provided via APP_TENANT_ID)
   *  2. Admin panel: use first key in map (superAdmin manages any tenant)
   */
  setFromClaims(claims: TenantAuthClaims): void {
    // Mobile app build: the tenantId is fixed at build time. If the user is NOT
    // a member of this app's tenant (cross-tenant login), do NOT switch the
    // active tenantId to a foreign tenant (it would corrupt every `collections`
    // read and crash the app). Keep `appTenantId` so public reads keep working,
    // but still publish the claims so the app can detect the non-membership
    // (see `belongsToAppTenant$`) and react (warn + logout).
    // STRICT membership (`claims.tenants?.[appTenantId]`): a superAdmin is NOT
    // exempt. In a tenant mobile app a superadmin with no role in `appTenantId`
    // is not a member of THIS app; switching `tenantId` to one of their other
    // tenants would corrupt every `collections` read (the original crash).
    if (this.appTenantId) {
      const isMember = !!claims.tenants?.[this.appTenantId];
      if (!isMember) {
        this.tenantIdSubject.next(this.appTenantId);
        this.claimsSubject.next(claims);
        return;
      }
    }
    const activeTenantId = this.resolveActiveTenantId(claims);
    this.tenantIdSubject.next(activeTenantId);
    this.claimsSubject.next(claims);
  }

  /**
   * Explicitly sets the active tenantId without requiring claims.
   * Used by the Member Portal, where the tenantId comes from the URL path
   * rather than from JWT claims (the portal is multi-tenant at runtime).
   */
  setTenantId(tenantId: string): void {
    this.tenantIdSubject.next(tenantId);
  }

  /**
   * Checks whether the user holds a specific role in the given tenant.
   * Considers both primary role (`tenants` map) and secondary roles
   * (`additionalRoles` map) for maximum flexibility.
   *
   * @example
   * // A player who is also a delegate
   * tenantCtx.hasRole('ffpcv', 'PLAYER')    // true
   * tenantCtx.hasRole('ffpcv', 'DELEGATE')  // true (additionalRoles)
   * tenantCtx.hasRole('ffpcv', 'ADMIN')     // false
   */
  hasRole(tenantId: string, role: string): boolean {
    const c = this.claimsSubject.getValue();
    if (!c) return false;
    if (c.tenants?.[tenantId] === role) return true;
    return c.additionalRoles?.[tenantId]?.includes(role) ?? false;
  }

  /**
   * Returns ALL roles the user holds in the given tenant
   * (primary + secondary roles combined).
   */
  getAllRoles(tenantId: string): string[] {
    const c = this.claimsSubject.getValue();
    if (!c) return [];
    const primary = c.tenants?.[tenantId];
    const additional = c.additionalRoles?.[tenantId] ?? [];
    const roles: string[] = [];
    if (primary) roles.push(primary);
    return [...new Set([...roles, ...additional])];
  }

  /**
   * Returns true when the user is a member of the given tenant
   * (has any role in it — regardless of primary or additional).
   */
  isMemberOf(tenantId: string): boolean {
    const c = this.claimsSubject.getValue();
    if (!c) return false;
    if (c.superAdmin) return true;
    return !!c.tenants?.[tenantId];
  }

  /**
   * Switches the active tenant to the given tenantId.
   * Only works when the user is a member of that tenant.
   * All reactive streams (activeRole$, tenantConfig$, menu) update automatically.
   */
  switchTenant(tenantId: string): void {
    const c = this.claimsSubject.getValue();
    if (!c?.tenants?.[tenantId]) return;
    this.tenantIdSubject.next(tenantId);
    localStorage.setItem(TenantContextService.STORAGE_KEY, tenantId);
  }

  private static readonly STORAGE_KEY = 'sp_active_tenant';

  private resolveActiveTenantId(claims: TenantAuthClaims): string | null {
    // Mobile app: prefer the build-time tenant if the user is a member
    if (
      this.appTenantId &&
      claims.tenants &&
      this.appTenantId in claims.tenants
    ) {
      return this.appTenantId;
    }
    // Restore last selected tenant from localStorage (admin panel)
    const stored = localStorage.getItem(TenantContextService.STORAGE_KEY);
    if (stored && claims.tenants?.[stored]) {
      return stored;
    }
    // Use first key in tenants map
    if (claims.tenants && typeof claims.tenants === 'object') {
      const keys = Object.keys(claims.tenants);
      return keys.length > 0 ? keys[0] : null;
    }
    return null;
  }

  clear(): void {
    // In a mobile app build, tenantId is fixed at build time — preserve it on logout
    // so that public Firestore reads still work without being authenticated.
    this.tenantIdSubject.next(this.appTenantId ?? null);
    this.claimsSubject.next(null);
    if (!this.appTenantId) {
      localStorage.removeItem(TenantContextService.STORAGE_KEY);
    }
  }

  /** Synchronous accessor — only safe after auth is resolved. */
  get tenantId(): string {
    const id = this.tenantIdSubject.getValue();
    if (!id) throw new Error('TenantContextService: tenantId not yet resolved');
    return id;
  }

  /** Synchronous accessor that returns null instead of throwing. */
  get tenantIdOrNull(): string | null {
    return this.tenantIdSubject.getValue();
  }

  get claims(): TenantAuthClaims | null {
    return this.claimsSubject.getValue() ?? null;
  }

  /** Returns the role of the user in the active tenant. */
  get activeRole(): string | null {
    const c = this.claimsSubject.getValue();
    if (!c) return null;
    const activeTenantId = this.resolveActiveTenantId(c);
    if (activeTenantId && c.tenants?.[activeTenantId]) {
      return c.tenants[activeTenantId];
    }
    return null;
  }

  get isSuperAdmin(): boolean {
    return this.claimsSubject.getValue()?.superAdmin === true;
  }

  /** Federation tenant ID from claims. Null when the club is not federated. */
  get federationId(): string | null {
    return this.claimsSubject.getValue()?.federationId ?? null;
  }

  /** Returns typed collection paths for the current tenant. */
  get collections() {
    return getCollections(this.tenantId);
  }
}
